import { useState } from "react";
import StatusEyebrow from "../components/StatusEyebrow";
import { useResendLink } from "../api/mutations";
import { CheckThinIcon, InfoCircleIcon } from "../icons";
import { t } from "copy/t";
import { isValidEmail, maskEmail } from "../format";

/*
 * Expired-link state with the self-serve re-mint form. Validation is inline
 * (no native reportValidity) to match the mockup. On a valid submit we POST the
 * confirmation email; rails always answers 202 (anti-enumeration) and mails a
 * fresh link to the batch's known address, so on success we swap the form for
 * the masked-email confirmation. A real server/network failure surfaces an
 * inline retry prompt (see useResendLink) rather than a false success.
 */
const ExpiredView = ({ token }) => {
  const [email, setEmail] = useState("");
  const [errorKey, setErrorKey] = useState(null); // 'invalid' | 'network' | null
  const [maskedTo, setMaskedTo] = useState("");
  const [sent, setSent] = useState(false);

  const resend = useResendLink(token, {
    onSuccess: () => setSent(true),
    onError: () => setErrorKey("network"),
  });

  const clearError = () => setErrorKey(null);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setErrorKey("invalid");
      return;
    }
    clearError();
    setMaskedTo(maskEmail(email));
    resend.mutate({ email });
  };

  const errorText =
    errorKey === "invalid"
      ? "Enter a valid email address so we can send your link."
      : "We couldn't send that just now — please try again in a moment.";

  return (
    <div className="card card-enter">
      <div className="card-pad">
        <StatusEyebrow dot="warn" tone="warn" text="Link expired" />
        <h1>This link has expired</h1>
        <p className="sub">
          Download links stay active for <b>90 days</b>. Yours has passed that
          window &mdash; but your gallery is safe. Enter your email and
          we&rsquo;ll send a fresh link right away.
        </p>

        {!sent && (
          <form onSubmit={onSubmit} noValidate>
            <div className="field">
              <label htmlFor="remintEmail">Email address</label>
              <div className="input-row">
                <input
                  className="input"
                  id="remintEmail"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  placeholder={t("placeholders.email")}
                  aria-describedby="remintErr"
                  aria-invalid={errorKey === "invalid" ? "true" : undefined}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errorKey) clearError();
                  }}
                />
                <button
                  type="submit"
                  className="btn btn-send"
                  disabled={resend.isLoading}
                >
                  <span className="lbl">Send me a new link</span>
                </button>
              </div>
              <p className="field-error" id="remintErr" role="alert" hidden={!errorKey}>
                <InfoCircleIcon size={15} />
                {errorText}
              </p>
            </div>
          </form>
        )}

        <div className={`remint-ok${sent ? " show" : ""}`} role="status" aria-live="polite">
          <CheckThinIcon size={20} />
          <p>
            A fresh link is on its way to <b>{maskedTo || "your inbox"}</b>. It
            can take a minute to arrive &mdash; check spam if you don&rsquo;t see
            it.
          </p>
        </div>

        <p className="support">
          Trouble getting in?{" "}
          <a href="mailto:help@movebooth.com">help@movebooth.com</a>
        </p>
      </div>
    </div>
  );
};

export default ExpiredView;
